import React, { useState } from "react";
import { useLoadScript, GoogleMap, Marker, Circle } from "@react-google-maps/api";
import "./clock.css";
import { useLanguage, localeFor } from "../i18n";

const mapContainerStyle = { width: "100%", height: "300px" };

// eslint-disable-next-line no-unused-vars
const ClockPanel = ({ clock, techLocation, t: _legacyT }) => {
  const { t, lang } = useLanguage();
  const loc = localeFor(lang);
  const {
    isClockedIn,
    handleClockToggle,
    handleLunchToggle,
    mapLocation,
    assignedSite,
    gpsAccuracy,
    isOnline,
    getETA,
    isInsideGeofence,
    getGeofenceStatus,
    geofenceEnabled,
    locationError,
    locationPermission,
    isLocating,
    lastSentAt,
    pendingOfflinePoints,
    refreshLocation,
  } = clock;
  const [showPastLogs, setShowPastLogs] = useState(false);
 

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
  });

  const geo = getGeofenceStatus ? getGeofenceStatus() : null;

  return (
    <div className="clock-panel">
      {/* Status Row */}
      <div className="status-row">
        <div className={`status-chip ${isOnline ? "online" : "offline"}`}>
        {isOnline ? t("Online") : t("Offline")}
        </div>

        {gpsAccuracy != null && (
          <div className="status-chip">
            {t("Accuracy")}: {Math.round(gpsAccuracy)}m
          </div>
        )}

        {isClockedIn && (
          <div className="status-chip sharing">
            {t("Location sharing on")}
            {lastSentAt ? ` · ${lastSentAt.toLocaleTimeString(loc, { hour: "numeric", minute: "2-digit" })}` : ""}
          </div>
        )}

        {pendingOfflinePoints > 0 && (
          <div className="status-chip offline">
            {t("{n} points waiting to upload", { n: pendingOfflinePoints })}
          </div>
        )}
      </div>

      {(locationError || locationPermission === "denied") && (
        <div className="location-alert">
          <div>
            {locationError ||
              t("Location is blocked for this app. Turn on Location for this website in your phone/browser settings.")}
          </div>
          <button
            className="clock-btn secondary"
            onClick={refreshLocation}
            disabled={isLocating}
          >
            {isLocating ? t("Locating…") : t("Retry Location")}
          </button>
        </div>
      )}

      {assignedSite && (
        <div className={`geofence-banner ${geo ? (geo.inside ? "inside" : "outside") : ""}`}>
          <strong>{assignedSite.name}</strong>
          {geofenceEnabled
            ? ` — ${t("clock-in only allowed on site")}`
            : ""}
          {geo && (
            <span>
              {" · "}
              {geo.inside ? t("On site") : t("Off site")}
              {` (${geo.distanceMeters}m)`}
            </span>
          )}
        </div>
      )}


      {/* Buttons */}
<div className="clock-buttons">
  <button
    className={`clock-btn ${isClockedIn ? "danger" : "primary"}`}
    onClick={handleClockToggle}
    disabled={isLocating}
  >
    {isClockedIn ? t("Clock Out") : t("Clock In")}
  </button>

  <button
    className={`clock-btn ${isClockedIn ? "warning" : "secondary"}`}
    onClick={handleLunchToggle}
  >
    {isClockedIn ? t("Lunch Off") : t("Lunch On")}
  </button>
</div>

{/* ⏱ Period Summary */}
{clock.stats && (
  <div style={{ marginTop: 10, fontSize: "0.95rem" }}>
    <div>
    <strong>
  {clock.stats.firstIsCurrent
    ? t("Current period")
    : t("Last period")}
</strong>
{" "}
      — {t("1st–14th ({month} 25th Pay Day)", { month: t(clock.stats.firstHalfPayMonth) })}
      <br />
      {t("Days")}: {clock.stats.firstHalfDays}
      &nbsp;|&nbsp;
      {t("Hours (no lunch)")}: {clock.stats.firstHalfHours}
      &nbsp;|&nbsp;
      {t("Hours (-1h/day lunch)")}: {clock.stats.firstHalfLunchHours}

    </div>

    <div style={{ marginTop: 6 }}>
      <strong>
        {clock.stats.secondIsCurrent ? t("Current period") : t("Last period")}
      </strong>{" "}
      — {t("15th–end ({month} 10th Pay Day)", { month: t(clock.stats.secondHalfPayMonth) })}
      <br />
      {t("Days")}: {clock.stats.secondHalfDays} &nbsp;|&nbsp;
      {t("Hours (no lunch)")}: {clock.stats.secondHalfHours}
      &nbsp;|&nbsp;
      {t("Hours (-1h/day lunch)")}: {clock.stats.secondHalfLunchHours}

    </div>
  </div>
)}

      {/* Map */}
      {!isLoaded ? (
        <p>{t("Loading map…")}</p>
      ) : !mapLocation ? (
        <p>{isLocating ? t("Fetching location…") : t("Location not available yet.")}</p>
      ) : (
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={{
          lat: mapLocation.latitude,
          lng: mapLocation.longitude,
        }}
        zoom={16}
      >
        <Marker
          position={{
            lat: mapLocation.latitude,
            lng: mapLocation.longitude,
          }}
          label={t("You")}
        />

        {assignedSite && (
          <>
            <Marker
              position={{
                lat: assignedSite.latitude,
                lng: assignedSite.longitude,
              }}
              label={assignedSite.name}
            />
            {techLocation && (
  <Marker
    position={techLocation}
    icon={{
      url: "/icons/house.png", // or tech icon
      scaledSize: new window.google.maps.Size(32, 32),
    }}
  />
)}

            <Circle
              center={{
                lat: assignedSite.latitude,
                lng: assignedSite.longitude,
              }}
              radius={assignedSite.radiusMeters}
              options={{
                fillColor: isInsideGeofence() ? "#00C851" : "#ff4444",
                fillOpacity: 0.2,
                strokeWeight: 2,
              }}
              
            />
          </>
        )}
      </GoogleMap>
      )}

      {/* ETA */}
      {assignedSite && getETA() && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          {t("Distance")}: {getETA().dist}m · {t("ETA: {n} min", { n: getETA().etaMinutes })}
        </div>
      )}
     <div style={{ display: "flex", justifyContent: "center", marginTop: 8 }}>
  <button
    className="clock-btn secondary"
    onClick={() => setShowPastLogs(true)}
  >
    {t("Past Time Logs")}

  </button>
</div>

{showPastLogs && (
  <div className="past-logs-popup">
    <div className="past-logs-popup-content">
      <button
        className="red-button"
        onClick={() => setShowPastLogs(false)}
      >
        {t("Close")}

      </button>

      <h3>{t("Past Time Logs")}</h3>


      <ul>
      {[...clock.timeLogs]
  .sort((a, b) => {
    const aTime = a.clockIn?.toDate ? a.clockIn.toDate() : a.clockIn;
    const bTime = b.clockIn?.toDate ? b.clockIn.toDate() : b.clockIn;
    return bTime - aTime;
  })
  .filter(
    (log) =>
      !(log.reason || "").toLowerCase().includes("off")
  )
  .map((log) => (
    <li key={log.id}>
  
  <strong>{t("Clock In")}:</strong>
  {" "}
    {(log.clockIn instanceof Date
      ? log.clockIn
      : log.clockIn.toDate()
    ).toLocaleString(loc)}
            {log.clockOut && (
              <>
                <br />
                <strong>{t("Clock Out")}:</strong>
{" "}
                {(log.clockOut?.toDate ? log.clockOut.toDate() : new Date(log.clockOut)).toLocaleString(loc)}
                <br />
                <strong>{t("Duration")}:</strong>
{" "}
                {log.duration} min
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  </div>
)}

    </div>
  );
};

export default ClockPanel;
